import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { IOrderRating } from 'app/core/models/order/rating';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IData, IDataPagination } from '../../models/api/data';
import { IDentistry } from '../../models/dentistry/dentistry';
import { ISentMaterialOrderList } from '../../models/dentistry/sent-material';
import { IError } from '../../models/error/error';
import { IOrderMedia } from '../../models/media/media';
import { IGetAllOrderItems, IOrderItem } from '../../models/order-item/order-item';
import {
    ICreateDentistryItem,
    ICreateMediaGroupByOrder,
    ICreateOrder,
    ICreateOrderRating,
    IDeleteDentistryItem,
    IDeleteMediaGroupByOrder,
    IGetAllDentistryItems,
    IGetAllMedias,
    IGetAllOrderDentistrySentMaterial,
    IGetDentistryItemDetail,
    IGetOrderDetail,
    IGetOrders,
    IOrder,
    IOrderList,
    IOrderMaterial, IPatient,
    IUpdateDentistryItem,
    IUpdateMediaGroupByOrder,
    IUpdateOrder, IUpdateOrderPatient,
} from '../../models/order/order';

export const urlGetOrders = () => ['clinic', 'admin', 'order'];
const urlCreateOrder = () => ['clinic', 'admin', 'order'];
const urlUpdateOrder = (orderId: string) => ['clinic', 'admin', 'order', orderId];
const getAllDentistryItem = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'dentistry-item'];
const urlCreateDentistryItem = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'dentistry-item'];
const urlUpdateDentistryItem = (orderId: string, dentistryItemId: string) => ['clinic', 'admin', 'order', orderId, 'dentistry-item', dentistryItemId];
const urlDeleteDentistryItem = (orderId: string, dentistryItemId: string) => ['clinic', 'admin', 'order', orderId, 'dentistry-item', dentistryItemId];
const urlGetAllOrderDentistrySentMaterial = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'sent-material'];
const urlCreateRating = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'rating'];
const urlGetOrder = (orderId: string) => ['clinic', 'admin', 'order', orderId];
const urlGetAllOrderItems = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'order-item'];
const urlGetAllOrderSentMaterial = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'sent-material'];
const urlOrderApproval = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'approve'];
const urlGetAllMedias = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'media'];
const urlCreateMedia = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'media'];
const urlDeleteOrder = (orderId: string) => ['clinic', 'admin', 'order', orderId];
const urlUpdateOrderPatient = (orderId: string) => ['clinic', 'admin', 'order', orderId, 'patient'];

// V2
const urlUpdateOrderV2 = (orderId: string) => ['v2', 'clinic', 'admin', 'order', orderId];


@Service()
export class OrderService {
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);

    public getOrders(parameters: IGetOrders): Observable<IDataPagination<IOrderList[]>> {
        const url = this.router.createUrlTree(urlGetOrders(), {
            queryParams: parameters,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IDataPagination<IOrderList[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public createOrder(data: ICreateOrder): Observable<IData<IOrder>> {
        const url = this.router.createUrlTree(urlCreateOrder());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IOrder>>(parsedUrl, data, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public updateOrder(data: IUpdateOrder): Observable<IData<IOrder>> {
        const { orderId, ...parameters } = data;

        const url = this.router.createUrlTree(urlUpdateOrder(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put<IData<IOrder>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getOrderDetail(data: IGetOrderDetail): Observable<IData<IOrder>> {
        const { orderId, ...queryParams } = data;

        const url = this.router.createUrlTree(urlGetOrder(orderId), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IOrder>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getAllDentistryItems(data: IGetAllDentistryItems): Observable<IData<IDentistry[]>> {
        const { orderId, ...queryParams } = data;
        const url = this.router.createUrlTree(getAllDentistryItem(orderId), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IDentistry[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public createDentistryItem(data: ICreateDentistryItem): Observable<IData<IDentistry>> {
        const { orderId, ...body } = data;

        const url = this.router.createUrlTree(urlCreateDentistryItem(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IDentistry>>(parsedUrl, body, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public updateDentistryItem(data: IUpdateDentistryItem): Observable<IData<IDentistry>> {
        const { orderId, dentistryItemId, ...body } = data;

        const url = this.router.createUrlTree(urlUpdateDentistryItem(orderId, dentistryItemId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put<IData<IDentistry>>(parsedUrl, body, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public deleteDentistryItem(data: IDeleteDentistryItem): Observable<null> {
        const { orderId, dentistryItemId, ...queryParams } = data;

        const url = this.router.createUrlTree(urlDeleteDentistryItem(orderId, dentistryItemId), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.delete(parsedUrl, { responseType: 'json' }).pipe(
            map((res: null) => {
                return res;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getAllOrderDentistrySentMaterial(data: IGetAllOrderDentistrySentMaterial): Observable<IData<IOrderMaterial[]>> {
        const { orderId, ...queryParams } = data;
        const url = this.router.createUrlTree(urlGetAllOrderDentistrySentMaterial(orderId), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IOrderMaterial[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getAllMedias(data: IGetAllMedias): Observable<IData<IOrderMedia[]>> {
        const { orderId, ...queryParams } = data;
        const url = this.router.createUrlTree(urlGetAllMedias(orderId), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IOrderMedia[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public createMedia(data: ICreateMediaGroupByOrder): Observable<IData<IOrderMedia[]>> {
        const { orderId, medias } = data;

        const url = this.router.createUrlTree(urlCreateMedia(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IOrderMedia[]>>(parsedUrl, medias, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public updateMedia({ orderId, orderMediaId, ...parameters }: IUpdateMediaGroupByOrder): Observable<IData<IOrderMedia>> {
        const baseUrl = ['clinic', 'admin', 'order', orderId, 'media', orderMediaId];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put<IData<IOrderMedia>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public deleteMedia({ orderId, mediaId }: IDeleteMediaGroupByOrder): Observable<null> {
        const baseUrl = ['clinic', 'admin', 'order', orderId, 'media', mediaId];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.delete<null>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    approveOrder(orderId: string): Observable<null> {
        const url = this.router.createUrlTree(urlOrderApproval(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<null>(parsedUrl, null, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    getAllOrderItems(data: IGetAllOrderItems): Observable<IData<IOrderItem[]>> {
        const { orderId, ...parameters } = data;

        const url = this.router.createUrlTree(urlGetAllOrderItems(orderId), {
            queryParams: {
                ...parameters,
            },
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IOrderItem[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    getDentistryItemDetail(data: IGetDentistryItemDetail): Observable<IData<IDentistry>> {
        const { orderId, dentistryItemId } = data;
        const baseUrl = ['clinic', 'admin', 'order', orderId, 'dentistry-item', dentistryItemId];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IDentistry>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    getAllSentMaterial(orderId: string): Observable<IData<ISentMaterialOrderList[]>> {
        const url = this.router.createUrlTree(urlGetAllOrderSentMaterial(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<ISentMaterialOrderList[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    deleteOrder(orderId: string): Observable<null> {
        const url = this.router.createUrlTree(urlDeleteOrder(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.delete<null>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    getOrderRating(orderId: string): Observable<IData<IOrderRating>> {
        const url = this.router.createUrlTree(urlCreateRating(orderId));
        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IOrderRating>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    createRating({ orderId, ...parameters }: ICreateOrderRating): Observable<IData<IOrderRating>> {
        const url = this.router.createUrlTree(urlCreateRating(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IOrderRating>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public updatePatient(data: IUpdateOrderPatient): Observable<IData<IPatient>> {
        const { orderId, ...parameters } = data;

        const url = this.router.createUrlTree(urlUpdateOrderPatient(orderId));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put<IData<IPatient>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    // V2
    public updateOrderV2(data: IUpdateOrder): Observable<IData<IOrder>> {
        const { orderId, ...parameters } = data;

        const url = this.router.createUrlTree(urlUpdateOrderV2(orderId));

        const parsedUrl = `${environment.apiUrl}${url}`;

        return this.httpClient.put<IData<IOrder>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }
}
